import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserSessionService } from './usersession.service';

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatReport {
  reportId: string;
  fileName: string;
  downloadUrl: string;
  expiresAt: string;
}

export interface ChatQuota {
  limit: number;
  used: number;
  remaining: number;
  resetsAt: string;
  enforced: boolean;
}

export interface ChatResult {
  requestId: string;
  sessionId: string;
  status: string;
  conversational: boolean;
  message: string;
  columns: string[];
  previewRows: any[];
  rowCount: number;
  report: ChatReport | null;
  quota: ChatQuota | null;
}

export interface ChatSessionSummary {
  sessionId: string;
  title: string;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

/** One logged turn of a saved thread, same shape as a live ChatResult plus the question. */
export interface ChatSessionMessage extends ChatResult {
  id: string;
  question: string;
  relevant: boolean;
  createdAt: string;
}

export interface ChatEnvelope {
  isSuccess: boolean;
  isInternalFailure: boolean;
  isException: boolean;
  failureReason: string | null;
  value: ChatResult[];
  quota: ChatQuota | null;
}

@Injectable({ providedIn: 'root' })
export class ChatbotService {
  constructor(private http: HttpClient, private session: UserSessionService) { }

  get enabled(): boolean {
    return !!environment.chatbotUrl;
  }

  ask(question: string, sessionId: string | null, history: ChatTurn[]): Observable<ChatEnvelope> {
    const headers = new HttpHeaders({ 'X-API-Key': environment.chatbotApiKey });
    return this.http.post<ChatEnvelope>(
      this.base() + '/api/integration/chat',
      {
        // The service resolves role + data scope from this id on every call.
        userId: this.session.userId(),
        question: question,
        statusType: 1,
        sessionId: sessionId,
        chatHistory: history.slice(-10)
      },
      { headers: headers }
    );
  }

  /** Saved threads for the logged-in user, most recent first (chat_sessions table). */
  sessions(): Observable<ChatSessionSummary[]> {
    return this.http.get<ChatSessionSummary[]>(
      this.base() + '/api/integration/sessions',
      { headers: this.headers(), params: { userId: String(this.session.userId()) } }
    );
  }

  /** Every logged turn of one thread, oldest first (query_logs table). */
  sessionMessages(sessionId: string): Observable<ChatSessionMessage[]> {
    return this.http.get<ChatSessionMessage[]>(
      this.base() + '/api/integration/sessions/' + encodeURIComponent(sessionId) + '/messages',
      { headers: this.headers(), params: { userId: String(this.session.userId()) } }
    );
  }

  deleteSession(sessionId: string): Observable<any> {
    return this.http.delete(
      this.base() + '/api/integration/sessions/' + encodeURIComponent(sessionId),
      { headers: this.headers(), params: { userId: String(this.session.userId()) } }
    );
  }

  private headers(): HttpHeaders {
    return new HttpHeaders({ 'X-API-Key': environment.chatbotApiKey });
  }

  /** downloadUrl is server-relative unless PUBLIC_BASE_URL is set on the service. */
  absoluteUrl(url: string): string {
    return /^https?:\/\//i.test(url) ? url : this.base() + url;
  }

  private base(): string {
    return environment.chatbotUrl.replace(/\/+$/, '');
  }
}
